// Shared room/session state and actions for the mode-specific UI components.
import { useEffect, useMemo, useRef, useState } from 'react';
import { getSocket } from '../socket';
import type { Color, Role, RoomMode, RoomState } from '../types';
import { boardFromFen, startBoard, type BoardMap, type PieceCode } from '../board/pieces';
import { parseTrainingSequence } from '../lecture';
import { remainingFromSnapshot } from '../../shared/clock.js';
import { createLectureActions, type PendingPromotion } from './lectureActions';
import { createGameActions } from './gameActions';
import { useRoomSocketLifecycle } from './roomSocketLifecycle';

const socket = getSocket();
const DEFAULT_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
const GRAPHIC_COLOR = '#b79a65';

type Screen = 'home' | 'workspace';

export function useAppController() {
  const [screen, setScreen] = useState<Screen>('home');
  const [role, setRole] = useState<Role>('trainer');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [roomInput, setRoomInput] = useState('');
  const [roomId, setRoomId] = useState('');
  const [state, setState] = useState<RoomState | null>(null);
  const [board, setBoard] = useState<BoardMap>(startBoard());
  const [animationVersion, setAnimationVersion] = useState(0);
  const [pendingGameMove, setPendingGameMove] = useState(false);
  const [orientation, setOrientation] = useState<'white' | 'black'>('white');
  const [selected, setSelected] = useState<string | null>(null);
  const [spare, setSpare] = useState<PieceCode | null>(null);

  const [recording, setRecording] = useState(false);
  const [recorded, setRecorded] = useState<string[]>([]);
  const [recordingStartFen, setRecordingStartFen] = useState(DEFAULT_FEN);

  const [lectureSeconds, setLectureSeconds] = useState(60);
  const lectureSecondsInputRef = useRef<HTMLInputElement>(null);
  const [gameMinutes, setGameMinutes] = useState(5);
  const [gameIncrement, setGameIncrement] = useState(3);
  const [trainerColor, setTrainerColor] = useState<Color>('w');
  const [startingTurn, setStartingTurn] = useState<Color>('w');
  const [selectedMode, setSelectedMode] = useState<RoomMode>('lecture');
  const [studentList, setStudentList] = useState<Record<string, string>>({});

  const [exerciseIndex, setExerciseIndex] = useState(0);
  const [exerciseFrozen, setExerciseFrozen] = useState(false);
  const [attempts, setAttempts] = useState<string[]>([]);
  const [studentEvents, setStudentEvents] = useState<string[]>([]);
  const [studentMoveHistory, setStudentMoveHistory] = useState<Array<{
    studentName: string;
    move: string;
    isCorrect: boolean;
  }>>([]);
  const [reviewHistory, setReviewHistory] = useState<RoomState['history']>([]);
  const [gameOver, setGameOver] = useState<{ winner: Color | 'draw' | null; reason: string | null } | null>(null);
  const [pendingPromotion, setPendingPromotion] = useState<PendingPromotion | null>(null);
  const [reviewIndex, setReviewIndex] = useState(-1);

  const [status, setStatus] = useState('Готово');
  const [connected, setConnected] = useState(socket.connected);
  const [monotonicNow, setMonotonicNow] = useState(() => performance.now());
  const [lectureClockSync, setLectureClockSync] = useState<{ serverNow: number; localMonotonicAtSync: number } | null>(null);
  const [gameClockSync, setGameClockSync] = useState<{ serverNow: number; localMonotonicAtSync: number } | null>(null);
  const [studentExerciseFen, setStudentExerciseFen] = useState(DEFAULT_FEN);
  const roomIdRef = useRef(roomId);
  const roleRef = useRef(role);
  const roomStateRef = useRef(state);
  const lastRestoredSocketIdRef = useRef<string | null>(null);
  const autoOrientationKeyRef = useRef<string | null>(null);

  roomIdRef.current = roomId;
  roleRef.current = role;
  roomStateRef.current = state;

  const steps = useMemo(() => parseTrainingSequence(state?.exercise?.sequence || ''), [state?.exercise?.sequence]);

  useEffect(() => {
    if (role !== 'student' || !state || state.mode === 'lecture') {
      autoOrientationKeyRef.current = null;
      return;
    }

    const key = state.mode + ':' + state.trainerColor;
    if (autoOrientationKeyRef.current === key) return;
    autoOrientationKeyRef.current = key;
    setOrientation(state.trainerColor === 'w' ? 'black' : 'white');
  }, [role, state?.mode, state?.trainerColor]);

  useEffect(() => {
    if (role !== 'trainer' || state?.mode !== 'lecture' || reviewHistory.length === 0) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      const target = event.target instanceof HTMLElement ? event.target : null;
      if (target?.closest('input, textarea, select, [contenteditable="true"]')) return;

      const nextIndex = Math.max(-1, Math.min(reviewHistory.length - 1, reviewIndex + (event.key === 'ArrowRight' ? 1 : -1)));
      if (nextIndex === reviewIndex) return;
      event.preventDefault();

      const fen = nextIndex < 0 ? DEFAULT_FEN : reviewHistory[nextIndex].fen;
      const nextTurn: Color = fen.split(/\s+/)[1] === 'b' ? 'b' : 'w';
      setReviewIndex(nextIndex);
      setBoard(boardFromFen(fen));
      setState((prev) => prev ? { ...prev, fen, turn: nextTurn } : prev);
      setStatus(nextIndex < 0 ? 'Начальная позиция' : 'Ход ' + reviewHistory[nextIndex].san);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [role, state?.mode, reviewHistory, reviewIndex]);

  useEffect(() => {
    const input = lectureSecondsInputRef.current;
    if (!input) return;

    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setLectureSeconds((value) => Math.min(3600, Math.max(1, value + (event.deltaY < 0 ? 5 : -5))));
    };

    input.addEventListener('wheel', handleWheel, { passive: false });
    return () => input.removeEventListener('wheel', handleWheel);
  }, [screen, role, roomId, state?.mode]);

  useRoomSocketLifecycle({
    refs: {
      roomId: roomIdRef,
      role: roleRef,
      lastRestoredSocketId: lastRestoredSocketIdRef,
      roomState: roomStateRef
    },
    state: {
      setState, setRoomId, setSelectedMode, setStartingTurn, setTrainerColor, setBoard,
      setReviewHistory, setReviewIndex, setLectureClockSync, setGameClockSync,
      setExerciseIndex, setExerciseFrozen, setAttempts, setStudentExerciseFen,
      setSelected, setSpare, setRole, setName, setScreen, setStatus, setConnected,
      setStudentList, setStudentEvents, setStudentMoveHistory, setAnimationVersion,
      setPendingPromotion, setRecording, setGameOver, setMonotonicNow, setPendingGameMove
    }
  });

  const lectureRemaining = state?.lectureTimer.active && lectureClockSync
    ? remainingFromSnapshot(state.lectureTimer.remainingMs, lectureClockSync, monotonicNow)
    : 0;

  const uiMode: RoomMode = role === 'trainer' ? selectedMode : (state?.mode || 'lecture');
  const lecture = uiMode === 'lecture';

  const displayedTurn: Color =
    lecture && role === 'student' && state?.exercise
      ? (steps[exerciseIndex]?.color || state.turn)
      : role === 'trainer' && selectedMode === 'standard' && !state?.gameTimer.active
        ? startingTurn
        : (state?.turn || 'w');

  let whiteClock = state?.gameTimer.remainingWhiteMs || 0;
  let blackClock = state?.gameTimer.remainingBlackMs || 0;
  if (state?.gameTimer.active && gameClockSync) {
    const elapsed = Math.max(0, monotonicNow - gameClockSync.localMonotonicAtSync);
    if (state.turn === 'w') whiteClock = Math.max(0, whiteClock - elapsed);
    else blackClock = Math.max(0, blackClock - elapsed);
  }

  function leaveToHome() {
    if (roomId) socket.emit('leaveRoom', { roomId });
    sessionStorage.clear();
    setRoomId('');
    setState(null);
    setBoard(startBoard());
    setSelected(null);
    setSpare(null);
    setRecorded([]);
    setReviewHistory([]);
    setStudentList({});
    setExerciseIndex(0);
    setExerciseFrozen(false);
    setAttempts([]);
    setStudentExerciseFen(DEFAULT_FEN);
    setStudentEvents([]);
    setStudentMoveHistory([]);
    setGameOver(null);
    setRecording(false);
    setStatus('Готово');
    setScreen('home');
  }

  function createRoom() {
    const trainerName = name.trim() || 'Тренер';
    if (!password) {
      setStatus('Введите пароль тренера');
      return;
    }

    sessionStorage.setItem('pvl-role', 'trainer');
    sessionStorage.setItem('pvl-name', trainerName);
    sessionStorage.removeItem('pvl-room');
    sessionStorage.removeItem('pvl-trainer-token');

    socket.emit('createRoom', {
      name: trainerName,
      password
    });
  }

  function joinRoom() {
    const id = roomInput.trim().toUpperCase();
    const studentName = name.trim() || 'Ученик';

    if (!id) {
      setStatus('Введите код комнаты');
      return;
    }

    sessionStorage.setItem('pvl-role', 'student');
    sessionStorage.setItem('pvl-room', id);
    sessionStorage.setItem('pvl-name', studentName);
    sessionStorage.removeItem('pvl-trainer-token');
    sessionStorage.removeItem('pvl-student-token');

    socket.emit('joinRoom', {
      roomId: id,
      studentName
    });
  }

  function setRoleAndWorkspace(next: Role) {
    setRole(next);
    setScreen('workspace');
    setName(next === 'trainer' ? 'Тренер' : 'Ученик');
  }

  function selectMode(nextMode: RoomMode) {
    if (!roomId || role !== 'trainer') return;

    setSelectedMode(nextMode);
    setGameOver(null);
    setPendingPromotion(null);
    socket.emit('changeMode', { roomId, mode: nextMode });
  }

  const lectureActions = createLectureActions({
    roomId, role, state, board, recording, recorded, recordingStartFen, lectureSeconds,
    lectureRemaining, selected, spare, setState, setBoard, setRecorded, setRecording,
    setRecordingStartFen, setLectureSeconds, setPendingPromotion, setSelected, setSpare,
    setStatus
  });

  const gameActions = createGameActions({
    roomId, role, state, recording, selectedMode, gameMinutes, gameIncrement, trainerColor,
    startingTurn, board, pendingGameMove, exerciseFrozen, exerciseIndex, steps,
    studentExerciseFen, isPromotionTarget: lectureActions.isPromotionTarget,
    setGameOver, setPendingPromotion, setBoard, setPendingGameMove, setSelected, setStatus
  });

  function boardClick(square: string) {
    if (role === 'trainer' && uiMode === 'lecture') {
      if (state?.mode !== 'lecture') {
        setSelected(null);
        setSpare(null);
        return;
      }
      lectureActions.trainerLectureClick(square);
      return;
    }

    if (role === 'student' && state?.mode === 'lecture') {
      if (!state.lectureTimer.active) {
        setSelected(null);
        setStatus('Дождитесь запуска таймера тренером.');
        return;
      }
      if (!selected) {
        if (board[square]) setSelected(square);
      } else {
        gameActions.studentStep(selected, square);
      }
      return;
    }

    if (!selected) {
        if (gameActions.canGamePiece(board[square])) setSelected(square);
      return;
    }

    gameActions.gameMove(selected, square);
  }

  function boardDrop(from: string, to: string) {
    if (from.startsWith('spare:')) {
      if (role !== 'trainer' || state?.mode !== 'lecture') return;
      const piece = from.slice(6) as PieceCode;
      const next = { ...board, [to]: piece };
      setSpare(null);
      lectureActions.publishBoard(next, state?.turn || 'w');
      return;
    }

    if (role === 'trainer' && uiMode === 'lecture') {
      if (state?.mode !== 'lecture') return;
      lectureActions.moveBoard(from, to);
      return;
    }

    if (state?.mode === 'lecture') {
    gameActions.studentStep(from, to);
      return;
    }

    gameActions.gameMove(from, to);
  }

  function completePromotion(piece: 'q' | 'r' | 'b' | 'n') {
    const pending = pendingPromotion;
    setPendingPromotion(null);
    if (!pending) return;

    if (pending.context === 'game') {
      gameActions.sendGameMove(pending.from, pending.to, piece);
      return;
    }

    if (pending.context === 'exercise') {
      gameActions.studentStep(pending.from, pending.to, piece);
      return;
    }

    if (role !== 'trainer' || state?.mode !== 'lecture' || !recording) return;
    const sourcePiece = board[pending.from];
    if (!sourcePiece || sourcePiece[0] !== pending.color) return;
    const next = { ...board };
    delete next[pending.from];
    next[pending.to] = (pending.color === 'w' ? 'w' : 'b') + piece.toUpperCase() as PieceCode;
    const moveColor = pending.color;
    setRecorded((prev) => [...prev, moveColor + ':' + pending.from + '-' + pending.to + '-' + piece]);
    lectureActions.localBoardState(next, moveColor === 'w' ? 'b' : 'w');
  }

  function drawArrow(from: string, to: string) {
    if (role !== 'trainer') return;
    socket.emit('drawArrow', {
      roomId,
      arrow: { from, to, color: GRAPHIC_COLOR }
    });
  }

  function toggleHighlight(square: string) {
    if (role !== 'trainer') return;
    socket.emit('toggleHighlight', { roomId, square, color: GRAPHIC_COLOR });
  }

  function clearGraphics() {
    if (role === 'trainer') socket.emit('clearGraphics', { roomId });
  }

  function exportReview() {
    socket.emit('exportGameToLecture', { roomId });
  }


  function undoLastMove() {
    socket.emit('undoLastMove', { roomId });
  }

  return {
    screen, role, name, password, roomInput, roomId, state, board, animationVersion, pendingGameMove,
    orientation, selected, spare, recording, recorded, lectureSeconds,
    lectureSecondsInputRef, gameMinutes, gameIncrement, trainerColor, startingTurn,
    selectedMode, studentList, exerciseIndex, exerciseFrozen, attempts, studentEvents,
    studentMoveHistory, reviewHistory, gameOver, pendingPromotion, reviewIndex, status,
    connected, lectureRemaining, uiMode, lecture, displayedTurn, whiteClock, blackClock,
    setRole, setName, setPassword, setRoomInput, setRoomId, setState, setBoard,
    setAnimationVersion, setOrientation, setSelected, setSpare, setRecording,
    setRecorded, setLectureSeconds, setGameMinutes, setGameIncrement, setTrainerColor,
    setStartingTurn, setSelectedMode, setStudentList, setExerciseIndex,
    setExerciseFrozen, setAttempts, setStudentEvents, setStudentMoveHistory,
    setReviewHistory, setGameOver, setPendingPromotion, setReviewIndex, setStatus, setPendingGameMove,
    setScreen, leaveToHome, createRoom, joinRoom, setRoleAndWorkspace, selectMode,
    ...lectureActions,
    ...gameActions, boardClick, boardDrop,
    completePromotion, drawArrow, toggleHighlight, clearGraphics, exportReview,
    undoLastMove
  };
}

